import { useState, useRef, useEffect } from "react";
import { Loader2, FileText, HelpCircle, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { SectionRename } from "./SectionRename";

interface Section {
  id: string;
  name: string;
  position: number;
  section_type: 'DOCUMENTS' | 'FAQ';
}

interface DocumentTabNavigationProps {
  sections: Section[];
  selectedSection: Section | null;
  onSectionSelect: (section: Section) => void;
  mandatorySections: string[];
  isLoading: boolean;
  onSectionRenamed?: (sectionId: string, newName: string) => void;
  onSectionDeleted?: () => void;
  isAdmin?: boolean;
}

export const DocumentTabNavigation = ({
  sections,
  selectedSection,
  onSectionSelect,
  mandatorySections,
  isLoading,
  onSectionRenamed,
  onSectionDeleted,
  isAdmin = false,
}: DocumentTabNavigationProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<{id: string, name: string} | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  
  const handleSectionRenamed = (sectionId: string, newName: string) => {
    if (onSectionRenamed) {
      onSectionRenamed(sectionId, newName);
    }
  };

  const checkScrollVisibility = (container: HTMLDivElement | null) => {
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftScroll(scrollLeft > 0);
    setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const desktopContainer = scrollContainerRef.current;
    const mobileContainer = mobileScrollRef.current;
    
    const handleScroll = (e: Event) => {
      checkScrollVisibility(e.target as HTMLDivElement);
    };

    if (desktopContainer) {
      checkScrollVisibility(desktopContainer);
      desktopContainer.addEventListener('scroll', handleScroll);
    }
    
    if (mobileContainer) {
      checkScrollVisibility(mobileContainer);
      mobileContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      desktopContainer?.removeEventListener('scroll', handleScroll);
      mobileContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [sections]);

  const scrollLeft = (container: HTMLDivElement | null) => {
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = (container: HTMLDivElement | null) => {
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleDeleteClick = (sectionId: string, sectionName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (mandatorySections.includes(sectionName)) {
      toast.error('Cannot delete mandatory sections');
      return;
    }
    setSectionToDelete({ id: sectionId, name: sectionName });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sectionToDelete) return;
    
    setIsDeleting(true);
    try {
      // Delete all documents in the section first
      const { data: documents } = await supabase
        .from('documents')
        .select('file_path')
        .eq('section_id', sectionToDelete.id);

      // Delete files from storage
      if (documents) {
        for (const doc of documents) {
          await supabase.storage
            .from('documents')
            .remove([doc.file_path]);
        }
      }

      // Delete the section and its documents (cascade delete should handle this)
      const { error } = await supabase
        .from('document_sections')
        .delete()
        .eq('id', sectionToDelete.id);

      if (error) throw error;

      toast.success('Section deleted successfully');
      onSectionDeleted?.();
      setIsDeleteDialogOpen(false);
      setSectionToDelete(null);
    } catch (error: any) {
      console.error('Error deleting section:', error);
      toast.error(error.message || 'Failed to delete section');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-[#637588]" />
      </div>
    );
  }

  return (
    <>
      {/* Desktop/Tablet tabs */}
      <div className="hidden md:block relative">
        {/* Left scroll indicator */}
        {showLeftScroll && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full bg-white shadow-sm hover:bg-gray-50"
              onClick={() => scrollLeft(scrollContainerRef.current)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Right scroll indicator */}
        {showRightScroll && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full bg-white shadow-sm hover:bg-gray-50"
              onClick={() => scrollRight(scrollContainerRef.current)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        <div 
          ref={scrollContainerRef}
          className="flex border-b border-[#dce0e5] px-4 gap-8 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {sections.map((section) => (
            <div
              key={section.id}
              className={`group flex flex-col items-center justify-center border-b-[3px] pb-[13px] pt-4 transition-colors min-w-fit ${
                selectedSection?.id === section.id
                  ? "border-b-[#111418] text-[#111418]"
                  : "border-b-transparent text-[#637588] hover:text-[#111418]"
              }`}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="flex items-center gap-2 cursor-pointer" 
                  onClick={() => onSectionSelect(section)}
                >
                  {section.section_type === 'FAQ' ? (
                    <HelpCircle className="h-4 w-4" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  {isAdmin ? (
                    <SectionRename
                      section={section}
                      onSectionRenamed={handleSectionRenamed}
                      mandatorySections={mandatorySections}
                    />
                  ) : (
                    <span className="text-sm font-bold leading-normal tracking-[0.015em]">
                      {section.name}
                    </span>
                  )}
                </div>
                {isAdmin && !mandatorySections.includes(section.name) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600"
                    onClick={(e) => handleDeleteClick(section.id, section.name, e)}
                    title="Delete section"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden relative">
        {/* Left scroll indicator for mobile */}
        {showLeftScroll && (
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent z-10 flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full bg-white shadow-sm"
              onClick={() => scrollLeft(mobileScrollRef.current)}
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
          </div>
        )}
        
        {/* Right scroll indicator for mobile */}
        {showRightScroll && (
          <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full bg-white shadow-sm"
              onClick={() => scrollRight(mobileScrollRef.current)}
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}
        
        <div 
          ref={mobileScrollRef}
          className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex space-x-6 min-w-max">
            {sections.map((section) => (
              <div key={section.id} className="group flex-shrink-0">
                <button
                  className={`flex flex-col items-center py-3 whitespace-nowrap transition-colors ${
                    selectedSection?.id === section.id
                      ? "text-gray-800 border-b-2 border-black font-semibold"
                      : "text-gray-500"
                  }`}
                  onClick={() => onSectionSelect(section)}
                >
                  {section.section_type === 'FAQ' ? (
                    <HelpCircle className="h-6 w-6 mb-1" />
                  ) : (
                    <FileText className="h-6 w-6 mb-1" />
                  )}
                  <span className="text-xs">{section.name}</span>
                </button>
                {isAdmin && !mandatorySections.includes(section.name) && selectedSection?.id === section.id && (
                  <div className="absolute mt-2 right-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600"
                      onClick={(e) => handleDeleteClick(section.id, section.name, e)}
                      title="Delete section"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Section"
        description={`Are you sure you want to delete "${sectionToDelete?.name}"? This will permanently delete all documents in this section. This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </>
  );
};